"use client";

import { BookOpen, ShieldCheck, Users, Waves } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Resources() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const features = [{
    icon: Waves,
    title: "Protecting Our Coastlines",
    description: "We provide essential guidance on preserving the delicate ecosystems of Protection Island and Dungeness, ensuring our sea birds thrive undisturbed."
  }, {
    icon: ShieldCheck,
    title: "Advocacy in Action",
    description: "Learn how to become a steward of the land. Our educational programs empower you to take meaningful steps toward wildlife conservation."
  }, {
    icon: BookOpen,
    title: "Educational Field Guides",
    description: "Access our comprehensive library of resources designed to inform the public about the unique species that call our refuges home."
  }, {
    icon: Users,
    title: "Community Stewardship",
    description: "Join a network of dedicated volunteers and advocates working together to secure a sustainable future for our national wildlife refuges."
  }];
  return <section data-section-id="1550" id="educational-resources" className="dark bg-secondary text-white relative overflow-hidden py-24 md:py-32">
      {}
      <svg aria-hidden="true" className="absolute -top-px left-0 w-full h-6 text-secondary rotate-180" viewBox="0 0 1440 24" preserveAspectRatio="none">
        <path d="M0,0 L0,12 L40,18 L90,8 L140,20 L200,10 L260,22 L320,8 L380,18 L450,6 L520,20 L590,10 L660,22 L730,8 L800,18 L870,6 L940,20 L1010,10 L1080,22 L1150,8 L1220,18 L1290,6 L1360,20 L1440,12 L1440,0 Z" fill="currentColor" />
      </svg>

      {}
      <svg aria-hidden="true" className="hidden md:block absolute top-24 right-12 w-14 h-14 text-blue-600 opacity-60" viewBox="0 0 60 60">
        <g stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none">
          <line x1="30" y1="6" x2="30" y2="18" />
          <line x1="30" y1="42" x2="30" y2="54" />
          <line x1="6" y1="30" x2="18" y2="30" />
          <line x1="42" y1="30" x2="54" y2="30" />
          <line x1="13" y1="13" x2="22" y2="22" />
          <line x1="38" y1="38" x2="47" y2="47" />
        </g>
      </svg>
      <svg aria-hidden="true" className="hidden md:block absolute bottom-32 left-10 w-12 h-12 text-green-600 opacity-70" viewBox="0 0 50 50">
        <path d="M25,5 L29,20 L44,22 L32,32 L36,46 L25,38 L14,46 L18,32 L6,22 L21,20 Z" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
      </svg>

      <div className="relative max-w-[1360px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className="lg:col-span-5 lg:sticky lg:top-24 self-start relative" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0) rotate(0deg)" : "translateY(28px) rotate(-3deg)",
          transition: "all 600ms cubic-bezier(0.34, 1.56, 0.64, 1)"
        }}>
            {}
            <div aria-hidden="true" className="absolute -top-10 -left-4 font-serif text-[14rem] md:text-[18rem] leading-none pointer-events-none select-none" style={{
            WebkitTextStroke: "2px hsl(120 61% 34% / 0.18)",
            color: "transparent"
          }}>
              01
            </div>

            <div className="relative">
              {}
              <div className="inline-flex items-center gap-2 bg-green-700 text-white border-[3px] border-white px-4 py-1.5 rounded-lg font-mono text-xs tracking-[0.16em] uppercase font-medium" style={{
              transform: "rotate(-3deg)",
              boxShadow: "4px 4px 0 hsl(210 100% 99%)"
            }}>
                Advocacy / Education
              </div>

              <h2 className="font-serif font-default mt-8 text-white" style={{
              fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
              letterSpacing: "-0.025em",
              lineHeight: 0.92
            }}>
                Protecting our{" "}
                <span className="relative inline-block">
                  <svg aria-hidden="true" className="absolute -inset-3 w-[calc(100%+1.5rem)] h-[calc(100%+1.5rem)] -z-0" viewBox="0 0 160 100" preserveAspectRatio="none">
                    <ellipse cx="80" cy="50" rx="72" ry="38" fill="none" stroke="hsl(240 100% 50%)" strokeWidth="5" strokeLinecap="round" style={{
                    transform: "rotate(-2deg)",
                    transformOrigin: "center"
                  }} />
                    <ellipse cx="80" cy="50" rx="68" ry="34" fill="none" stroke="hsl(240 100% 50%)" strokeWidth="3" strokeLinecap="round" opacity="0.7" style={{
                    transform: "rotate(1deg)",
                    transformOrigin: "center"
                  }} />
                  </svg>
                  <span className="relative z-10">refuges</span>
                </span>
                .
              </h2>

              <p className="mt-8 text-white/80 max-w-xl" style={{
              fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)",
              lineHeight: 1.55
            }}>
                Friends of the Refuges provides the knowledge and tools you need to protect our wildlife and sea bird refuges, ensuring these vital habitats remain safe for generations to come.
              </p>

              <div className="mt-10">
                <Link to="/AdvocacyEducationPage">
                  <Button className="bg-blue-600 text-white border-[3px] border-white hover:bg-blue-700 rounded-lg px-7 py-6 font-mono text-sm tracking-[0.12em] uppercase font-semibold h-auto" style={{
                  boxShadow: "5px 5px 0 hsl(120 61% 34%)"
                }}>
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-7 flex flex-col">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            const isLast = i === features.length - 1;
            return <div key={i} data-index={i} className="group relative" style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? "translateY(0) rotate(0deg)" : "translateY(28px) rotate(-3deg)",
              transition: `all 600ms cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 80}ms`
            }}>
                  <div className="grid grid-cols-[auto_1fr] gap-5 md:gap-7 py-8 md:py-10">
                    {}
                    <div className="relative">
                      <div className="w-16 h-16 rounded-lg border-[3px] border-white bg-secondary flex items-center justify-center text-blue-500" style={{
                    boxShadow: "4px 4px 0 hsl(120 61% 34%)"
                  }}>
                        <Icon className="w-8 h-8" strokeWidth={2.5} />
                      </div>
                      {}
                      <svg aria-hidden="true" className="absolute -top-2 -right-2 w-5 h-5 text-green-500" viewBox="0 0 24 24">
                        <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none">
                          <line x1="12" y1="3" x2="12" y2="8" />
                          <line x1="12" y1="16" x2="12" y2="21" />
                          <line x1="3" y1="12" x2="8" y2="12" />
                          <line x1="16" y1="12" x2="21" y2="12" />
                          <line x1="6" y1="6" x2="9" y2="9" />
                          <line x1="15" y1="15" x2="18" y2="18" />
                        </g>
                      </svg>
                    </div>

                    {}
                    <div>
                      <h3 className="font-serif font-default text-white inline-block relative" style={{
                    fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)",
                    letterSpacing: "-0.015em",
                    lineHeight: 1.0
                  }}>
                        {feature.title}
                        <svg aria-hidden="true" className="absolute left-0 -bottom-2 w-full h-2 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-[250ms]" viewBox="0 0 200 8" preserveAspectRatio="none">
                          <path d="M2,4 Q20,1 40,4 T80,4 T120,4 T160,4 T198,4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                      </h3>
                      <p className="mt-4 text-white/70" style={{
                    fontSize: "1.0625rem",
                    lineHeight: 1.6
                  }}>
                        {feature.description}
                      </p>
                    </div>
                  </div>

                  {}
                  {!isLast && <svg aria-hidden="true" className="w-full h-2 text-blue-600/60" viewBox="0 0 600 8" preserveAspectRatio="none">
                      <path d="M2,4 Q30,1 60,4 T120,4 T180,4 T240,4 T300,4 T360,4 T420,4 T480,4 T540,4 T598,4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>}
                </div>;
          })}
          </div>
        </div>
      </div>

      {}
      <svg aria-hidden="true" className="absolute -bottom-px left-0 w-full h-6 text-secondary" viewBox="0 0 1440 24" preserveAspectRatio="none">
        <path d="M0,0 L0,12 L40,18 L90,8 L140,20 L200,10 L260,22 L320,8 L380,18 L450,6 L520,20 L590,10 L660,22 L730,8 L800,18 L870,6 L940,20 L1010,10 L1080,22 L1150,8 L1220,18 L1290,6 L1360,20 L1440,12 L1440,0 Z" fill="currentColor" />
      </svg>
    </section>;
}